/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import ImageIcon from "@mui/icons-material/Image";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  AccordionSummary,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Cesium from "cesium";
import React from "react";
import { Choose, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useMap3DObjectApi } from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { CesiumImageryItem } from "../types/CesiumMapItems";
import { DrawerItem } from "./CesiumDrawerDataItems";

interface Props {
  item: DrawerItem<CesiumImageryItem>;
  viewer: Cesium.Viewer;
}

const loadImagery = (
  url: string,
  viewer: Cesium.Viewer,
  token: string,
): Cesium.ImageryLayer | undefined => {
  try {
    if (!token) return undefined;
    const resource = new Cesium.Resource({
      url,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      retryAttempts: 0,
    });
    const provider = new Cesium.TileMapServiceImageryProvider({
      url: resource,
    });
    provider.errorEvent.addEventListener((err: Cesium.TileProviderError) => {
      if (
        (err.error as unknown as Cesium.RequestErrorEvent)?.statusCode !== 404
      ) {
        console.error(err);
      }
    });
    const res = viewer.imageryLayers.addImageryProvider(provider);
    return res;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const CesiumDrawerImageryItem = ({
  item,
  viewer,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const { getMapAreaMapObject } = useMap3DObjectApi();
  const [loadId, setLoadId] = React.useState<number>();
  const imageryLayer = React.useRef<Cesium.ImageryLayer>();
  const [isShowing, setIsShowing] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const { isLoading: backendLoading } = useCustomQuery(
    { key: "map-area-map-object", id: loadId },
    () => getMapAreaMapObject(loadId!),
    {
      enabled: !!loadId,
      onSuccess: (d) => {
        if (!d.url || !d.accessToken) return;
        imageryLayer.current = loadImagery(d.url, viewer, d.accessToken);
        setLoaded(true);
        setIsShowing(true);
      },
    },
  );

  const handleVisibilityClick = (val: boolean) => {
    if (!imageryLayer.current) return;
    imageryLayer.current.show = val;
    setIsShowing(val);
  };

  const handleGoToClick = () => {
    if (!imageryLayer.current) return;
    viewer.zoomTo(imageryLayer.current);
  };

  const handleDownloadClick = () => {
    if (!loadId) setLoadId(item.mapItem.id);
  };

  const handleRemoveClick = () => {
    if (!imageryLayer.current) return;
    viewer.imageryLayers.remove(imageryLayer.current);
    imageryLayer.current = undefined;
    setLoaded(false);
    setLoadId(undefined);
  };

  const loading = React.useMemo(() => backendLoading, [backendLoading]);

  return (
    <Accordion
      expanded={false}
      elevation={1}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      square
      sx={{
        backgroundColor: (theme) => theme.palette.primary.semiLight,
        color: (theme) => theme.palette.primary.contrastText,
        width: "100%",
      }}
    >
      <AccordionSummary sx={{ cursor: "default", alignItems: "center" }}>
        <Grid container justifyContent="center">
          <Grid
            item
            container
            xs
            flexDirection="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={{ flexBasis: "100%" }}
          >
            <Grid item sx={{ mr: 1 }}>
              <Tooltip title={t("imagery").toString()}>
                <Avatar
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                  }}
                >
                  <ImageIcon />
                </Avatar>
              </Tooltip>
            </Grid>
            <Grid item>
              <Typography sx={{ m: 1, wordBreak: "break-all" }}>
                {item.mapItem.name}
              </Typography>
            </Grid>
          </Grid>
          <If
            condition={loaded}
            render={() => (
              <>
                <Tooltip title={t("move-to").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoToClick();
                      }}
                      size="large"
                    >
                      <MyLocationIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
                {isShowing ? (
                  <Tooltip title={t("hide").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibilityClick(false);
                        }}
                        size="large"
                      >
                        <VisibilityOffIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                ) : (
                  <Tooltip title={t("show").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibilityClick(true);
                        }}
                        size="large"
                      >
                        <VisibilityIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                )}
              </>
            )}
          />
          <Choose>
            <Choose.When
              condition={!loading && !loaded}
              render={() => (
                <Tooltip title={t("download-data").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadClick();
                      }}
                      size="large"
                    >
                      <GetAppIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
            <Choose.When
              condition={loading}
              render={() => (
                <Grid>
                  <CircularProgress
                    size={24}
                    sx={{
                      margin: (theme) => `auto ${theme.spacing(1)}`,
                      color: (theme) => theme.palette.primary.contrastText,
                    }}
                  />
                </Grid>
              )}
            />
            <Choose.When
              condition={!loading && loaded}
              render={() => (
                <Tooltip title={t("remove-from-map").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveClick();
                      }}
                      size="large"
                    >
                      <CloseIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
          </Choose>
        </Grid>
      </AccordionSummary>
    </Accordion>
  );
};
